import {reactive, Reactive, watch} from "vue";
import {message} from "ant-design-vue";
import {Rule, useForm} from "ant-design-vue/es/form";
import {
    IWechatMerchantBank,
    IWechatMerchantBankBranch,
    IWechatMerchantCity,
    IWechatMerchantProvince,
    WechatMerchant
} from "@/model/wechat_merchant";
import {IFormState} from "@/lib/types";
import {api} from "@/lib/api";

export interface IWMABusinessInfo {
    merchant_shortname: string,
    service_phone: string,
    biz_store_name: string,
    biz_store_code: string,
    biz_store_address: string,
    store_entrance_pic: IWMAMedia,
    indoor_pic: IWMAMedia,
}

export interface IWMAMedia {
    media_id: string,
    url: string,
}

export interface IWMAIdCardFront extends IWMAMedia {
    nationality: string,
    name: string,
    id: string,
    gender: string,
    addr: string,
}

export interface IWMAIdCardBackend extends IWMAMedia {
    valid_date: string,
}

export interface IWMALicense extends IWMAMedia {
    type_of_organization: string,
    type_of_enterprise: string,
    serial: string,
    registered_date: string,
    registered_capital: string,
    reg_num: string,
    paid_in_capital: string,
    legal_representative: string,
    enterprise_name: string,
    business_scope: string,
    address: string,
    subject_type: 'SUBJECT_TYPE_ENTERPRISE' | 'SUBJECT_TYPE_INDIVIDUAL' | null,
}

export interface IWMABankAccountInfo extends IWechatMerchantBank {
    bank_account_type: 'BANK_ACCOUNT_TYPE_CORPORATE' | 'BANK_ACCOUNT_TYPE_PERSONAL'
    account_number: string,
    province: IWechatMerchantProvince
    city: IWechatMerchantCity
    branch: IWechatMerchantBankBranch
}

export interface IWMSettlementInfo {
    settlement_id: string, // 写ID，如：719
    qualification_type: string, // 写名称，如：餐饮
    activities_id: string, // 固定 20191030111cff5b5e
    activities_rate: string, // 固定 0.2
}

export interface IWMContactInfo {
    mobile_phone: string,
    contact_email: string,
}

export interface IWechatMerchantApplymentInfo {
    license: IWMALicense,
    id_card_front: IWMAIdCardFront
    id_card_backend: IWMAIdCardBackend
    business_info: IWMABusinessInfo
    bank_account_info: IWMABankAccountInfo
    settlement_info: IWMSettlementInfo
    contact_info: IWMContactInfo
}

export interface IWechatMerchantApplymentRejectReason {
    field: string
    field_name: string
    reject_reason: string
}

export type TWechatMerchantApplymentStatus =
    'APPLYMENT_STATE_EDITTING'
    | 'APPLYMENT_STATE_AUDITING'
    | 'APPLYMENT_STATE_REJECTED'
    | 'APPLYMENT_STATE_TO_BE_CONFIRMED'
    | 'APPLYMENT_STATE_TO_BE_SIGNED'
    | 'APPLYMENT_STATE_SIGNING'
    | 'APPLYMENT_STATE_FINISHED'
    | 'APPLYMENT_STATE_CANCELED'

export const WechatMerchantApplymentStatusColor: { [key: string]: string } = {
    'APPLYMENT_STATE_EDITTING': 'default',
    'APPLYMENT_STATE_AUDITING': 'processing',
    'APPLYMENT_STATE_REJECTED': 'error',
    'APPLYMENT_STATE_TO_BE_CONFIRMED': 'processing',
    'APPLYMENT_STATE_TO_BE_SIGNED': 'processing',
    'APPLYMENT_STATE_SIGNING': 'processing',
    'APPLYMENT_STATE_FINISHED': 'success',
    'APPLYMENT_STATE_CANCELED': 'default',
}
export const WechatMerchantApplymentStatusMessage: { [key: string]: string } = {
    'APPLYMENT_STATE_EDITTING': '编辑中',
    'APPLYMENT_STATE_AUDITING': '审核中',
    'APPLYMENT_STATE_REJECTED': '已驳回',
    'APPLYMENT_STATE_TO_BE_CONFIRMED': '待账户验证',
    'APPLYMENT_STATE_TO_BE_SIGNED': '待签约',
    'APPLYMENT_STATE_SIGNING': '开通权限中',
    'APPLYMENT_STATE_FINISHED': '已完成',
    'APPLYMENT_STATE_CANCELED': '已作废',
}

export interface IWechatMerchantApplyment {
    business_code: string,
    agent_id: string,
    info: IWechatMerchantApplymentInfo
    state: TWechatMerchantApplymentStatus,
    state_msg: string | null,
    reject_reasons: IWechatMerchantApplymentRejectReason[] | null
    sign_url: string | null,
    merchant_id: string | null,
    profit_sharing_max_ratio: number | null,
    submit_at: string | null,
    update_at: string,
    create_at: string,
    profit_sharing_request_at: string | null,
}

export interface ISegmentForm {
    entity: IFormState;
    business: IFormState;
    payment: IFormState;
}

type segKey = 'entity' | 'business' | 'payment' | ''

export class WechatMerchantApplyment {
    readonly applyment: Reactive<IWechatMerchantApplyment>
    readonly state = reactive({
        open: {license: false, idCardFront: false, idCardBackend: false},
        loading: false, rules: {
            entity: {} as Record<string, Rule[]>,
            business: {} as Record<string, Rule[]>,
            payment: {} as Record<string, Rule[]>,
        }, steps: [
            {title: '主体信息', description: '证件、资质', status: 'process', key: 'entity' as segKey},
            {title: '经营信息', description: '名称、场地、电话', status: 'process', key: 'business' as segKey},
            {title: '结算信息', description: '收款账号、费率', status: 'process', key: 'payment' as segKey},
        ]
    })
    readonly segment: ISegmentForm

    constructor(readonly merchant: WechatMerchant, applyment: IWechatMerchantApplyment) {
        this.applyment = reactive(applyment)
        this.state.rules.entity = {
            'license.media_id': [{required: true, message: '营业执照必传'}],
            'license.reg_num': [{
                required: true,
                validator: async (_r: unknown, v: string) => {
                    if (v == '') return Promise.reject('执照编码必填')
                    else if (/(^(?:(?![IOZSV])[\dA-Z]){2}\d{6}(?:(?![IOZSV])[\dA-Z]){10}$)|(^\d{15}$)/.test(v)) return Promise.resolve()
                    else return Promise.reject('执照编码格式错误')
                }
            }],
            'license.legal_representative': [{
                validator: async (_r: unknown, v: string) => {
                    if (v == '') return Promise.reject('法人必填')
                    else return Promise.resolve()
                }
            }],
            'license.address': [{required: true, message: '地址必填'}],

            'id_card_front.media_id': [{required: true, message: '法人身份证正面照必传'}],
            'id_card_front.id': [{
                validator: async (_r: unknown, v: string) => {
                    if (v == '') return Promise.reject('身份证号码必填')
                    else if (/^\d{6}(18|19|20)?\d{2}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])\d{3}(\d|X|x)$/.test(v)) return Promise.resolve()
                    else return Promise.reject('身份证号码错误')
                }
            }],
            'id_card_front.name': [{required: true, message: '名称必填'}],
            'id_card_backend.media_id': [{required: true, message: '法人身份证背面照必传'}],
            'id_card_backend.valid_date': [{required: true, message: '身份证有效期必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (v == '') return Promise.reject('身份证有效期必填')
                    else {
                        // 20060804-20260804
                        const arr = v.split('-')
                        if (arr.length != 2) return Promise.reject('身份证有效期格式错误')
                        else {
                            if (/^\d{8}$/.test(arr[0])) return Promise.resolve()
                            else return Promise.reject('身份证有效期格式错误')
                        }
                    }
                }
            }],
        }
        this.state.rules.business = {
            'business_info.store_entrance_pic.media_id': [{required: true, message: '门头照必传'}],
            'business_info.indoor_pic.media_id': [{required: true, message: '店内照必传'}],
            'business_info.merchant_shortname': [{required: true, message: '商户简称必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (/^\s|\s$/.test(v)) return Promise.reject('前后不能有空格')
                    else if (v.length < 2) return Promise.reject('内容过短')
                    else if (v.length > 64) return Promise.reject('内容过长')
                    return Promise.resolve()
                }
            }],
            'business_info.service_phone': [{required: true, message: '客服电话必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (/^1[3456789]\d{9}$/.test(v)) return Promise.resolve()
                    else if (/^[0-9-+]{5,20}$/.test(v)) return Promise.resolve()
                    else return Promise.reject('客服电话格式错误')
                }
            }],
            'business_info.biz_store_name': [{required: true, message: '店名必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (/^\s|\s$/.test(v)) return Promise.reject('前后不能有空格')
                    else if (v.length < 2) return Promise.reject('内容过短')
                    else if (v.length > 50) return Promise.reject('内容过长')
                    return Promise.resolve()
                }
            }],
            'business_info.biz_store_code': [{required: true, message: '门店所在地区必填'}],
            'business_info.biz_store_address': [{required: true, message: '门店地址必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (/^\s|\s$/.test(v)) return Promise.reject('前后不能有空格')
                    else if (v.length < 4) return Promise.reject('内容过短')
                    else if (v.length > 512) return Promise.reject('内容过长')
                    return Promise.resolve()
                }
            }],
        }
        this.state.rules.payment = {
            'license.subject_type': [{required: true, message: '主体类型必选'}],
            'settlement_info.qualification_type': [{required: true, message: '行业类型必选'}],
            'bank_account_info.bank_account_type': [{required: true, message: '账号类型必选'}],
            'bank_account_info.account_number': [{required: true, message: '银行账号必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (this.applyment.info.bank_account_info.bank_account_type == 'BANK_ACCOUNT_TYPE_PERSONAL' && !/^([1-9]{1})(\d{15}|\d{18})$/.test(v)) return Promise.reject('个人储蓄卡卡号格式错误')
                    else if (this.applyment.info.bank_account_info.bank_account_type == 'BANK_ACCOUNT_TYPE_CORPORATE' && !/^[0-9]{12,20}$/.test(v)) return Promise.reject('对公账户号格式错误')
                    return Promise.resolve()
                }
            }],
            'settlement_info.activities_id': [{required: true, message: '优惠费率活动必选'}],
            'bank_account_info.bank_alias': [{required: true, message: '开户银行必选'}],
            'bank_account_info.branch.bank_branch_name': [{
                validator: async (_r: unknown, v: string) => {
                    if (this.applyment.info.bank_account_info.need_bank_branch && !v) return Promise.reject('支行必选')
                    return Promise.resolve()
                }
            }],
            'contact_info.mobile_phone': [{required: true, message: '手机号码必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (/^1[3456789]\d{9}$/.test(v)) return Promise.resolve()
                    return Promise.reject('手机号码格式错误')
                }
            }],
            'contact_info.contact_email': [{required: true, message: '邮箱必填'}, {
                validator: async (_r: unknown, v: string) => {
                    if (/^[a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(v)) return Promise.resolve()
                    return Promise.reject('邮箱格式错误')
                }
            }],
        }
        this.segment = {
            entity: useForm(this.applyment.info, this.state.rules.entity),
            business: useForm(this.applyment.info, this.state.rules.business),
            payment: useForm(this.applyment.info, this.state.rules.payment)
        }

        this.state.open.license = !!applyment.info.license.media_id
        this.state.open.idCardFront = !!applyment.info.id_card_front.media_id
        this.state.open.idCardBackend = !!applyment.info.id_card_backend.media_id
        watch(() => this.applyment.info, this.syncSteps, {deep: true})
    }

    syncSteps = () => {
        this.state.steps = [
            {title: '主体信息', description: '证件、资质', status: 'process', key: 'entity' as segKey},
            {title: '经营信息', description: '名称、场地、电话', status: 'process', key: 'business' as segKey},
            {title: '结算信息', description: '收款账号、费率', status: 'process', key: 'payment' as segKey},
        ]
        if (this.applyment.state === 'APPLYMENT_STATE_EDITTING')
            this.state.steps.unshift({title: '准备材料', description: '收集证件照片、信息', status: 'finish', key: ''})
        this.state.steps.forEach(i => {
            if (i.key !== '') this.segment[i.key].validate().then(() => {
                i.status = 'finish'
            }).catch(() => {
                if (i.key) this.segment[i.key].clearValidate()
                const info = this.applyment.info
                switch (i.key) {
                    case 'entity':
                        i.status = (!info.license.media_id && !info.id_card_front.media_id && !info.id_card_backend.media_id) ? 'wait' : 'process'
                        break
                    case 'business':
                        i.status = (!info.business_info.indoor_pic.media_id && !info.business_info.store_entrance_pic.media_id && !info.business_info.merchant_shortname && !info.business_info.service_phone && !info.business_info.biz_store_name && !info.business_info.biz_store_code && !info.business_info.biz_store_address) ? 'wait' : 'process'
                        break
                    case 'payment':
                        i.status = (!info.license.subject_type && !info.settlement_info.qualification_type &&
                            !info.bank_account_info.bank_account_type && !info.bank_account_info.account_number &&
                            !info.bank_account_info.bank_alias && !info.contact_info.mobile_phone && !info.contact_info.contact_email) ? 'wait' : 'process'
                        break
                }
            })
        })
    }

    ocrLicense(url: string) {
        this.state.loading = true
        const done = message.loading('识别中', 0)
        api<void, IWMALicense>({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/ocr/license`,
            method: 'POST',
            data: {url}
        }).then(res => {
            this.applyment.info.license = res
            this.state.open.license = true
        }).catch(() => this.ocrLicenseClean()).finally(() => {
            done()
            this.state.loading = false
        })
    }

    ocrLicenseClean() {
        this.applyment.info.license = {
            media_id: '',
            url: '',
            type_of_organization: '',
            type_of_enterprise: '',
            serial: '',
            registered_date: '',
            registered_capital: '',
            reg_num: '',
            paid_in_capital: '',
            legal_representative: '',
            enterprise_name: '',
            business_scope: '',
            address: '',
            subject_type: null,
        }
    }

    ocrIDFront(url: string) {
        this.state.loading = true
        const done = message.loading('识别中', 0)
        api<void, IWMAIdCardFront>({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/ocr/id/card/front`,
            method: 'POST',
            data: {url}
        }).then(res => {
            this.applyment.info.id_card_front = res
            this.state.open.idCardFront = true
        }).catch(() => this.ocrIDFrontClean()).finally(() => {
            done()
            this.state.loading = false
        })
    }

    ocrIDFrontClean() {
        this.applyment.info.id_card_front = {
            addr: '',
            gender: '',
            name: '',
            nationality: '',
            url: '',
            id: '',
            media_id: ''
        }
    }

    ocrIDBackend(url: string) {
        this.state.loading = true
        const done = message.loading('识别中', 0)
        api<void, IWMAIdCardBackend>({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/ocr/id/card/backend`,
            method: 'POST',
            data: {url}
        }).then(res => {
            this.applyment.info.id_card_backend = res
            this.state.open.idCardBackend = true
        }).catch(() => this.ocrIDBackendClean()).finally(() => {
            done()
            this.state.loading = false
        })
    }

    ocrIDBackendClean() {
        this.applyment.info.id_card_backend = {media_id: '', url: '', valid_date: ''}
    }


    handleStoreEntranceMedia(url: string) {
        this.state.loading = true
        const done = message.loading('处理中', 0)
        api<void, IWMAMedia>({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/media`,
            method: 'POST',
            data: {url}
        }).then(res => this.applyment.info.business_info.store_entrance_pic = res)
            .catch(() => this.applyment.info.business_info.store_entrance_pic.url = '').finally(() => {
            done()
            this.state.loading = false
        })
    }

    handleIndoorMedia(url: string) {
        this.state.loading = true
        const done = message.loading('处理中', 0)
        api<void, IWMAMedia>({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/media`,
            method: 'POST',
            data: {url}
        }).then(res => {
            this.applyment.info.business_info.indoor_pic = res
        }).catch(() => this.applyment.info.business_info.indoor_pic.url = '').finally(() => {
            done()
            this.state.loading = false
        })
    }

    sync() {
        this.state.loading = true
        const done = message.loading('更新中', 0)
        api<void, IWechatMerchantApplyment>({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/sync`,
        }).then(res => Object.assign(this.applyment, res)).finally(() => {
            done()
            this.state.loading = false
        })
    }

    requestProfitSharing() {
        this.state.loading = true
        const done = message.loading('更新中', 0)
        api({url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/request/profit/sharing`}).then(() => {
            message.success('已发出').then()
            this.applyment.profit_sharing_request_at = new Date().toISOString()
        }).finally(() => {
            done()
            this.state.loading = false
        })
    }

    syncProfitSharingRate() {
        this.state.loading = true
        const done = message.loading('保存中', 0)
        api<void, IWechatMerchantApplyment>({url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/sync/profit/sharing/rate`})
            .then(rs => Object.assign(this.applyment, rs))
            .finally(() => {
                done()
                this.state.loading = false
            })
    }

    save() {
        this.state.loading = true
        const done = message.loading('保存中', 0)
        api({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/info`,
            method: 'PUT',
            data: this.applyment.info
        }).then(() => {
            message.success('保存成功').then()
        }).finally(() => {
            done()
            this.state.loading = false
        })
    }

    delete() {
        this.state.loading = true
        const done = message.loading('保存中', 0)
        api({url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}`, method: 'DELETE'}).then(() => {
            delete this.merchant.applyments[this.applyment.business_code]
            return message.success('删除成功')
        }).finally(() => {
            done()
            this.state.loading = false
        })
    }

    async submit() {
        await Promise.all(this.state.steps.filter(i => i.key != '').map(i => new Promise((resolve, reject) => {
            if (!i.key) return
            this.segment[i.key].validate().then(() => {
                i.status = 'finish'
                resolve('')
            }).catch((e) => {
                i.status = 'error'
                reject(e)
            })
        })))
        this.state.loading = true
        await api({
            url: `user/${this.merchant.agent.meta.account.agent_id}/merchant/applyment/${this.applyment.business_code}/submit`,
            method: 'POST',
            data: this.applyment.info
        })
        this.applyment.state = 'APPLYMENT_STATE_AUDITING'
    }

    data():IWechatMerchantApplyment{
        return JSON.parse(JSON.stringify(this.applyment))
    }
}